import { Button } from "@/components/custom/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "@/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import React, { useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TbEdit } from "react-icons/tb";
import SelectInput from "@/components/select-input/select-input";
import { isDefined } from "@/lib/utils";

import { showActionErrorToast } from "@/state/error-toast";

const userFormSchema = z.object({
  firstName: z.string().min(1, { message: "Please enter a valid name" }),
  lastName: z.string().min(1, { message: "Please enter a valid name" }),
  // nullable: /me types defaultAccount as number, but the column is Int? and the value is returned
  // verbatim, so null does reach the form. z.number().optional() rejects null, which would fail the
  // whole schema and make Save a silent no-op.
  defaultAccount: z.number().nullable().optional(),
  profileImg: z.instanceof(File).optional(),
});

interface EditUserDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditUserDetails = ({
  open,
  onOpenChange,
}: EditUserDetailsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const account = useQuery(client.users.me.asQueryOptions());
  // Returns this user's active accounts, or every active account when they're a SuperAdmin -- the
  // endpoint already branches on that server-side. Shares its query key with the header's
  // AccountPicker, so this is served from cache rather than costing an extra request.
  const relatedAccounts = useQuery(
    client.users.getRelatedAccounts.asQueryOptions(),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(
    account.data?.profileImg,
  );

  const accountOptions = React.useMemo(
    () =>
      (relatedAccounts?.data ?? []).map((a) => ({
        label: a?.account?.name,
        value: a?.account?.id,
      })),
    [relatedAccounts?.data],
  );
  // Only users who actually have a choice get the field: multiple accounts, or SuperAdmin. Derived
  // from data rather than from isSuccess -- gating on isSuccess hid the field entirely whenever the
  // query errored or left the success state, which is worse than showing it in a failed state. The
  // loading/error states are surfaced on the control itself below instead.
  const showDefaultAccount =
    account.data?.role === "SuperAdmin" || accountOptions.length > 1;

  const form = useForm<z.infer<typeof userFormSchema>>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: account.data?.firstName || "",
      lastName: account.data?.lastName || "",
      defaultAccount: account.data?.defaultAccount,
    },
  });

  // Seed from /me exactly once per mount, rather than on every account.data identity change. A
  // dirty-check guard was wrong in both directions: re-seeding discards an in-progress edit and its
  // dirty flag, while skipping the re-seed left the modal showing stale values for as long as it
  // stayed open. Seeding once is unambiguous, and safe because this component is remounted on every
  // open (see -user-avatar.tsx), so each open starts from fresh server data.
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (account.data && !seeded.current) {
      seeded.current = true;
      form.reset({
        firstName: account.data.firstName || "",
        lastName: account.data.lastName || "",
        defaultAccount: account.data.defaultAccount,
      });
      setPreview(account.data.profileImg);
    }
  }, [account.data, form]);

  const updateUser = useMutation({
    ...client.users.updateUser.asMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: client.users.me.queryKey });
      toast.success("Profile updated successfully");
      onOpenChange(false);
    },
    onError: (err) => {
      showActionErrorToast(err, "Failed to update profile");
    },
  });

  const onSubmit = async (data: z.infer<typeof userFormSchema>) => {
    const { dirtyFields } = form.formState;
    // Send only fields the user actually changed. Echoing unchanged server values back is what let a
    // plain name edit fail: it resent a defaultAccount that may since have become invalid, and the
    // backend rejection is surfaced as a toast on this mutation.
    //
    // Gated on dirtyFields ALONE -- deliberately not on showDefaultAccount. That flag is derived from
    // live query state, so folding it in here meant a selection the user really made could be
    // silently dropped if the accounts query changed state between picking and saving.
    const formData = {
      ...(dirtyFields.firstName ? { firstName: data.firstName } : {}),
      ...(dirtyFields.lastName ? { lastName: data.lastName } : {}),
      ...(dirtyFields.profileImg && data.profileImg
        ? { profileImg: data.profileImg }
        : {}),
      ...(dirtyFields.defaultAccount && isDefined(data.defaultAccount)
        ? { defaultAccount: data.defaultAccount }
        : {}),
    };

    // Unreachable in the UI (Save is disabled while the form is pristine), kept as a guard so a
    // pristine submit can never fire a no-op PATCH that would toast a false "updated".
    if (Object.keys(formData).length === 0) return;

    try {
      await updateUser.mutateAsync({ formData });
    } catch {
      // Surfaced by the mutation's onError toast; caught so the rejection isn't unhandled.
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // shouldDirty is required: onSubmit now sends only dirty fields, and setValue does not mark the
      // field dirty by default -- without this the avatar would never be uploaded.
      form.setValue("profileImg", file, { shouldDirty: true });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="whitespace-pre-line max-w-[30vw]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{t("userProfile")}</DialogTitle>
            </DialogHeader>
            <div className="p-6">
              <div className="flex justify-center mb-6">
                <div
                  className="relative cursor-pointer"
                  onMouseEnter={() => setIsHovering(true)}
                  onMouseLeave={() => setIsHovering(false)}
                  onClick={handleEditClick}
                >
                  <Avatar size={"lg"}>
                    <AvatarImage src={preview} />
                    <AvatarFallback className="font-medium text-[16.67px]">
                      {form.watch("firstName")?.slice(0, 1)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {isHovering && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <TbEdit className="text-white" size={24} />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="py-2">
                    <FormLabel>{t("firstName")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("")} {...field} />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.firstName?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="py-2">
                    <FormLabel>{t("lastName")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("")} {...field} />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.lastName?.message}
                    </FormMessage>
                  </FormItem>
                )}
              />

              {showDefaultAccount && (
                <FormField
                  control={form.control}
                  name="defaultAccount"
                  render={({ field }) => (
                    <FormItem className="py-2">
                      <FormLabel>{t("defaultAccount")}</FormLabel>
                      <FormControl>
                        <SelectInput
                          options={accountOptions}
                          // ?? null, not undefined: undefined flips react-select into uncontrolled
                          // mode, after which the displayed label can diverge from the form value.
                          value={
                            accountOptions.find(
                              (a) => a.value === field.value,
                            ) ?? null
                          }
                          // An explicit placeholder matters here: the house default is "", so an
                          // unmatched value (null default, or an account no longer in the list)
                          // would otherwise render as a completely blank control with no hint.
                          placeholder={t("selectAccount")}
                          isLoading={relatedAccounts.isLoading}
                          isMulti={false}
                          isClearable={false}
                          onChange={(option) => field.onChange(option?.value)}
                        />
                      </FormControl>
                      <FormMessage>
                        {relatedAccounts.isError
                          ? t("failedToLoadAccounts")
                          : form.formState.errors.defaultAccount?.message}
                      </FormMessage>
                    </FormItem>
                  )}
                />
              )}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button">{t("cancel")}</Button>
              </DialogClose>
              {/* Disabled while pristine so "Save" can never report success without having saved
                  anything -- submitting an unchanged form used to close the dialog with a toast. */}
              <Button
                variant={"azure"}
                type="submit"
                disabled={!form.formState.isDirty || updateUser.isPending}
              >
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
